import { useState, useEffect, useCallback } from 'react';
import { useStoryStatus } from './useStoryStatus.ts';

export type MainStep = 'characters' | 'scenes' | 'videos' | 'final';
export type SubStep = 'fetching' | 'generating' | 'complete';

interface MainStepState {
  status: 'locked' | 'unlocked' | 'active' | 'completed';
  substeps: SubStepState[];
}

interface SubStepState {
  id: SubStep;
  status: 'pending' | 'active' | 'completed';
  label: string;
}

interface ProgressState {
  mainSteps: Record<MainStep, MainStepState>;
  activeMainStep: MainStep | null;
  isComplete: boolean;
}

const initialState = (): ProgressState => ({
  mainSteps: {
    characters: {
      status: 'unlocked',
      substeps: [
        { id: 'fetching', status: 'pending', label: 'Getting your story...' },
        { id: 'generating', status: 'pending', label: 'Generating characters...' },
        { id: 'complete', status: 'pending', label: 'Characters ready!' }
      ]
    },
    scenes: {
      status: 'locked',
      substeps: [
        { id: 'generating', status: 'pending', label: 'Creating scenes...' },
        { id: 'complete', status: 'pending', label: 'Scenes ready!' }
      ]
    },
    videos: {
      status: 'locked',
      substeps: [
        { id: 'generating', status: 'pending', label: 'Creating videos...' },
        { id: 'complete', status: 'pending', label: 'Videos ready!' }
      ]
    },
    final: {
      status: 'locked',
      substeps: [
        { id: 'complete', status: 'pending', label: 'Final story ready!' }
      ]
    }
  },
  activeMainStep: 'characters',
  isComplete: false
});

// Helper function for progress state equality
const equalsProgress = (a: ProgressState, b: ProgressState): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

const useProgressTracker = (storyId?: string, currentPage?: string) => {
  const [state, setState] = useState<ProgressState>(initialState);
  
  // Use the centralized status hook - it will automatically refetch when storyId changes
  const { status: statusData } = useStoryStatus(
    storyId ? parseInt(storyId) : null
  );
  
  // Extract data with fallbacks
  const storyStatus = statusData?.status;
  const storyCurrentStep = statusData?.current_step;
  const hasFinalVideo = statusData?.has_final_video || false;
  const characterPhase = statusData?.character_phase;
  const characterCount = statusData?.character_count || 0;

  // Debug logging for storyId, storyStatus, storyCurrentStep changes
  useEffect(() => {
    console.log('useProgressTracker: Story data changed', {
      storyId,
      storyStatus,
      storyCurrentStep,
      hasFinalVideo,
      characterPhase,
      characterCount
    });
  }, [storyId, storyStatus, storyCurrentStep, hasFinalVideo, characterPhase, characterCount]);

  // Reset progress state whenever storyId changes (no previousStoryId logic)
  useEffect(() => {
    if (storyId) {
      console.log('useProgressTracker: Story changed, resetting progress for story:', storyId);
      setState(initialState());
    }
  }, [storyId]);

  // Reset when story status indicates a new story is starting
  useEffect(() => {
    if (storyStatus === 'queued' || storyStatus === 'processing' || storyStatus === 'generating_characters') {
      if (process.env.NODE_ENV !== 'production') {
        console.log('useProgressTracker: New story starting, resetting progress', { storyStatus });
      }
      setState(prev => {
        const next = initialState();
        return equalsProgress(prev, next) ? prev : next;
      });
    }
  }, [storyStatus]);

  // Normalization function to keep main step status and substeps consistent
  const normalize = (s: ProgressState): ProgressState => {
    (Object.keys(s.mainSteps) as MainStep[]).forEach(ms => {
      const step = s.mainSteps[ms];
      if (!Array.isArray(step.substeps) || step.substeps.length === 0) return;
      
      if (step.status === 'completed') {
        step.substeps.forEach(ss => ss.status = 'completed');
      }
      if (step.status === 'active' && !step.substeps.some(ss => ss.status === 'active')) {
        const firstPending = step.substeps.find(ss => ss.status === 'pending');
        if (firstPending) firstPending.status = 'active';
      }
    });
    return s;
  };

  // Update main step states based on story progress
  useEffect(() => {
    if (!storyId) return;

    setState(() => {
      const s = initialState();
      
      // If we don't have status data yet, just return initial state
      if (!storyStatus || !storyCurrentStep) {
        return s;
      }
      
      // Early shortcut when final video exists
      if (hasFinalVideo) {
        s.mainSteps.characters.status = 'completed';
        s.mainSteps.scenes.status = 'completed';
        s.mainSteps.videos.status = 'completed';
        s.mainSteps.final.status = 'completed';
        s.mainSteps.characters.substeps.forEach(ss => ss.status = 'completed');
        s.mainSteps.scenes.substeps.forEach(ss => ss.status = 'completed');
        s.mainSteps.videos.substeps.forEach(ss => ss.status = 'completed');
        s.mainSteps.final.substeps[0].status = 'completed';
        s.activeMainStep = null;
        s.isComplete = true;
        return normalize(s);
      }

      // Update main step statuses based on storyCurrentStep
      switch (storyCurrentStep) {
        case 'characters':
          s.mainSteps.characters.status = 'active';
          s.mainSteps.scenes.status = 'locked';
          s.mainSteps.videos.status = 'locked';
          s.mainSteps.final.status = 'locked';
          s.activeMainStep = 'characters';
          break;
          
        case 'scenes':
        case 'generating_frames':
          s.mainSteps.characters.status = 'completed';
          s.mainSteps.scenes.status = 'active';
          s.mainSteps.videos.status = 'locked';
          s.mainSteps.final.status = 'locked';
          s.activeMainStep = 'scenes';
          break;
          
        case 'videos':
        case 'generating_videos':
          s.mainSteps.characters.status = 'completed';
          s.mainSteps.scenes.status = 'completed';
          s.mainSteps.videos.status = 'active';
          s.mainSteps.final.status = 'locked';
          s.activeMainStep = 'videos';
          break;
          
        case 'final':
          s.mainSteps.characters.status = 'completed';
          s.mainSteps.scenes.status = 'completed';
          s.mainSteps.videos.status = 'completed';
          s.mainSteps.final.status = 'active';
          s.activeMainStep = 'final';
          break;
      }

      // Update substep states with proper precedence using client-side inference
      const hasScenes = (['scenes', 'videos', 'final'].includes(storyCurrentStep)) ||
                        (['generating_frames', 'generating_videos', 'completed'].includes(storyStatus));
      const hasVideos = hasFinalVideo ||
                        (['videos', 'final'].includes(storyCurrentStep)) ||
                        (['generating_videos', 'completed'].includes(storyStatus));

      // Characters - use character_phase for more accurate substep control
      if (characterPhase === 'creating_story') {
        s.mainSteps.characters.substeps[0].status = 'active'; // fetching
      } else if (characterPhase === 'generating_characters') {
        s.mainSteps.characters.substeps[0].status = 'completed'; // fetching
        s.mainSteps.characters.substeps[1].status = 'active'; // generating
        // Update the label to include character count
        s.mainSteps.characters.substeps[1].label = `Generating ${characterCount} characters...`;
      } else if (characterPhase === 'characters_complete') {
        s.mainSteps.characters.substeps[0].status = 'completed'; // fetching
        s.mainSteps.characters.substeps[1].status = 'completed'; // generating
        s.mainSteps.characters.substeps[2].status = 'completed'; // complete
      }
      // Scenes: status OR fallback when the user is on Scenes
      else if (storyStatus === 'generating_frames' || storyCurrentStep === 'scenes') {
        s.mainSteps.characters.substeps.forEach(ss => ss.status = 'completed');

        if (hasScenes) {
          s.mainSteps.scenes.substeps.forEach(ss => ss.status = 'completed');
        } else {
          s.mainSteps.scenes.substeps[0].status = 'active';
        }
      }
      // Videos: status OR fallback when the user is on Videos
      else if (storyStatus === 'generating_videos' || storyCurrentStep === 'videos') {
        s.mainSteps.characters.substeps.forEach(ss => ss.status = 'completed');
        s.mainSteps.scenes.substeps.forEach(ss => ss.status = 'completed');

        if (hasVideos) {
          s.mainSteps.videos.substeps.forEach(ss => ss.status = 'completed');
        } else {
          s.mainSteps.videos.substeps[0].status = 'active';
        }
      }
      // Final / Completed
      else if (storyStatus === 'completed' || storyCurrentStep === 'final') {
        s.mainSteps.characters.substeps.forEach(ss => ss.status = 'completed');
        s.mainSteps.scenes.substeps.forEach(ss => ss.status = 'completed');
        s.mainSteps.videos.substeps.forEach(ss => ss.status = 'completed');

        if (hasFinalVideo) {
          s.mainSteps.final.substeps[0].status = 'completed';
          s.mainSteps.final.status = 'completed';
          s.activeMainStep = null;
          s.isComplete = true;
        } else {
          s.mainSteps.final.status = 'active';
          s.mainSteps.final.substeps[0].status = 'pending';
          s.activeMainStep = 'final';
          s.isComplete = false;
        }
      }

      // Dev-only invariants
      if (process.env.NODE_ENV !== 'production') {
        const { scenes, videos } = s.mainSteps;
        if ((videos.status === 'active' || videos.status === 'completed') && scenes.status !== 'completed') {
          console.warn('Invariant: videos active/completed but scenes not completed');
        }
      }

      return normalize(s);
    });
  }, [storyId, storyStatus, storyCurrentStep, hasFinalVideo, characterPhase, characterCount]);

  const getActiveSubsteps = useCallback(() => {
    // Always prioritize the current page's substeps - don't auto-switch based on story progress
    if (currentPage) {
      const seg = currentPage?.split('/').filter(Boolean)[0] as 'characters'|'scenes'|'videos'|'final'|'final-story'|undefined;
      const pageStep = seg === 'final-story' ? 'final' : seg;
      
      if (pageStep && ['characters', 'scenes', 'videos', 'final'].includes(pageStep) && state.mainSteps[pageStep as MainStep]?.status !== 'locked') {
        const pageSubsteps = state.mainSteps[pageStep as MainStep]?.substeps;
        if (pageSubsteps && pageSubsteps.length > 0) {
          return pageSubsteps;
        }
      }
    }
    
    // Fallback to characters substeps with current status
    return state.mainSteps.characters?.substeps || [
      { id: 'fetching' as SubStep, status: 'active' as const, label: 'Getting your story...' },
      { id: 'generating' as SubStep, status: 'pending' as const, label: 'Generating characters...' },
      { id: 'complete' as SubStep, status: 'pending' as const, label: 'Characters ready!' }
    ];
  }, [state.mainSteps, currentPage]);

  const shouldShowSubsteps = useCallback(() => {
    // Hide substeps when story is complete (has final video URL)
    if (statusData?.final_url) {
      return false;
    }
    // Always show substeps while story is in progress
    return true;
  }, [statusData?.final_url]);

  return {
    // State
    mainSteps: state.mainSteps,
    activeMainStep: state.activeMainStep,
    isComplete: state.isComplete,
    activeSubsteps: getActiveSubsteps(),
    shouldShowSubsteps: shouldShowSubsteps(),
    
    // Helpers
    isMainStepUnlocked: (step: MainStep) => state.mainSteps[step].status !== 'locked',
    isMainStepActive: (step: MainStep) => state.mainSteps[step].status === 'active',
    isMainStepCompleted: (step: MainStep) => state.mainSteps[step].status === 'completed',
  };
};

export default useProgressTracker;